import { __makeTemplateObject, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React, { useState, useRef, useEffect } from 'react';
import { Box, themeGet, ToggleSwitch } from '@primer/react';
import styled from 'styled-components';
import './index.css.js';
import { createUniqueID, isEnterOrSpacePressed } from '../../utils/functions.js';

/**
 *
 * @param {string} ariaLabel
 * @param {boolean} checked (optional)(controlled) - If the switch is checked or not
 *  * If this prop is passed, the switch will be controlled
 * @param {boolean} defaultState (optional) - The default state of the switch
 *  * To be used if the switch is uncontrolled
 * @param {boolean} disabled (optional) - If the switch is disabled or not
 * @param {object} extraStyle (optional) - Any extra styles to be applied to the switch
 * @param {string} id (optional) - The id of the switch
 * @param {string} label - Switch Label
 * @param {boolean} isDarkMode (optional) - If the switch is in dark mode theme or not
 * @param {function} onValueChange - The function to be called when the value of the switch changes
 * @param {number} tabIndex (optional) - The tab index of the switch
 * @param {string} variant (optional) - The variant of the switch
 */
var LTSwitchContainer = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  gap: 6px;\n  width: auto;\n  &:focus-visible {\n    .switch {\n      outline: 2px solid;\n      outline-color: ", ";\n      outline-offset: 2px;\n    }\n  }\n"], ["\n  display: flex;\n  align-items: center;\n  gap: 6px;\n  width: auto;\n  &:focus-visible {\n    .switch {\n      outline: 2px solid;\n      outline-color: ", ";\n      outline-offset: 2px;\n    }\n  }\n"])), themeGet("colors.focus.outlineColor"));
var LTCustomSwitch = styled.span(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  background: ", ";\n  border: 1px solid;\n  border-color: ", ";\n"], ["\n  background: ", ";\n  border: 1px solid;\n  border-color: ", ";\n"])), themeGet("colors.slate.bg"), themeGet("colors.border.default"));
var LTSwitch = function LTSwitch(_a) {
  var _b = _a.ariaLabel,
    ariaLabel = _b === void 0 ? undefined : _b,
    _c = _a.ariaLabelledBy,
    ariaLabelledBy = _c === void 0 ? undefined : _c,
    _d = _a.ariaDescribedBy,
    ariaDescribedBy = _d === void 0 ? undefined : _d,
    _e = _a.checked,
    checked = _e === void 0 ? null : _e,
    _f = _a.defaultState,
    defaultState = _f === void 0 ? false : _f,
    _g = _a.disabled,
    disabled = _g === void 0 ? false : _g,
    extraStyle = _a.extraStyle,
    _h = _a.id,
    id = _h === void 0 ? undefined : _h,
    _j = _a.isDarkMode,
    isDarkMode = _j === void 0 ? false : _j,
    _k = _a.label,
    label = _k === void 0 ? "" : _k,
    _l = _a.labelId,
    labelId = _l === void 0 ? undefined : _l,
    _m = _a.onValueChange,
    onValueChange = _m === void 0 ? function () {} : _m,
    _o = _a.switchStyle,
    switchStyle = _o === void 0 ? {} : _o,
    _p = _a.tabIndex,
    tabIndex = _p === void 0 ? 0 : _p,
    _q = _a.variant,
    variant = _q === void 0 ? LTSwitch.Variant.DEFAULT : _q;
  // This state is maintained just to handle border color of the switch
  var _r = useState(checked !== null && checked !== void 0 ? checked : defaultState),
    isChecked = _r[0],
    setIsChecked = _r[1];
  var uniqueId = useState(id || createUniqueID("ltswitch_"))[0];
  var isControlled = checked !== null;
  var switchRef = useRef(null);
  useEffect(function () {
    setAttributesForDefaultSwitch();
  }, []);
  useEffect(function () {
    var parentDiv = document.getElementById(uniqueId);
    var buttonEl = parentDiv === null || parentDiv === void 0 ? void 0 : parentDiv.querySelector("button");
    if (buttonEl) {
      ariaLabel && buttonEl.setAttribute("aria-label", ariaLabel);
      buttonEl.setAttribute("tabindex", tabIndex);
    }
  }, [isChecked, ariaLabel, tabIndex]);
  useEffect(function () {
    if (isControlled) {
      setIsChecked(checked);
    }
  }, [checked]);
  var handleSquareSwitchClick = function handleSquareSwitchClick() {
    // This is added to prevent multiple calls to onValueChange
    isControlled && onValueChange(!checked);
  };
  var handleDefaultSwitchClick = function handleDefaultSwitchClick() {
    var node = switchRef === null || switchRef === void 0 ? void 0 : switchRef.current;
    if (node && !disabled) {
      var currentState = node.getAttribute("aria-checked") === "true";
      var newState = String(!currentState);
      node.setAttribute("aria-checked", newState);
      onValueChange(!currentState);
    }
  };
  var setAttributesForDefaultSwitch = function setAttributesForDefaultSwitch() {
    var domNode = switchRef === null || switchRef === void 0 ? void 0 : switchRef.current;
    var isSwitchChecked = isControlled ? checked : defaultState;
    if (domNode) {
      domNode.setAttribute("aria-checked", isSwitchChecked);
      domNode.setAttribute("aria-disabled", disabled);
    }
  };
  var toggleSwitch = function toggleSwitch(value) {
    // If the switch is controlled for square variant, handling in done via onClick
    if (variant === LTSwitch.Variant.DEFAULT || !isControlled) {
      setIsChecked(value);
      onValueChange(value);
    }
  };
  var handleKeydown = function handleKeydown(e) {
    if (variant === LTSwitch.Variant.DEFAULT && isEnterOrSpacePressed(e)) {
      e.preventDefault();
      handleDefaultSwitchClick();
    }
  };
  __assign({
    button: {
      boxShadow: "0px 2px 4px 0px #8C959F26",
      border: "1px solid",
      borderColor: "border.default",
      "&:hover": {
        backgroundColor: disabled ? "switchTrack.disabledBg" : ""
      },
      "&:focus-visible": {
        outlineColor: "checked.bg.checked"
      }
    },
    "&> span": {
      display: "none"
    }
  }, extraStyle);
  return variant === LTSwitch.Variant.SQUARE ? /*#__PURE__*/React.createElement(ToggleSwitch, __assign({
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy
  }, isControlled && {
    checked: checked
  }, {
    defaultChecked: defaultState,
    disabled: disabled,
    id: uniqueId,
    onClick: handleSquareSwitchClick,
    onChange: toggleSwitch,
    onKeyDown: handleKeydown,
    size: "small",
    type: "checkbox"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LTSwitchContainer, {
    "aria-labelledby": labelId,
    "aria-describedby": ariaDescribedBy,
    "aria-checked": isControlled ? checked : defaultState,
    "aria-disabled": disabled,
    className: "".concat(isDarkMode ? 'dark-mode' : '', " lt-default-switch"),
    id: id || uniqueId,
    onClick: handleDefaultSwitchClick,
    onKeyDown: handleKeydown,
    ref: switchRef,
    role: "switch",
    sx: __assign({}, switchStyle),
    tabIndex: tabIndex
  }, /*#__PURE__*/React.createElement(LTCustomSwitch, {
    className: "switch"
  }, /*#__PURE__*/React.createElement("span", null)), label && /*#__PURE__*/React.createElement("span", {
    className: "label",
    id: labelId
  }, label)));
};
LTSwitch.Variant = {
  DEFAULT: "default",
  SQUARE: "square"
};
var templateObject_1, templateObject_2;

export { LTSwitch as default };
